import { RefObject, useCallback, useEffect, useRef } from 'react';
import {
  CARD_TOP_BORDER_PX,
  HERO_SLOT_MIN_PX,
  cardTopPull,
  heroSlotMax,
  phoneThreadHeight,
} from './hero-dock';
import { CARD_HEADER_GAP_PX, KEYBOARD_GAP_PX, KEYBOARD_OPEN_THRESHOLD_PX, keyboardInsetPx } from './search-bar-viewport';
import { readSettledLayout } from './settled-layout';

/**
 * Phones, hero composer only: the conversation thread in the card's top region
 * fills the screen between the header and the bottom (or the keyboard).
 *
 * Writes two things onto the capsule, both read by CSS:
 *  - `--card-top-h`, how far the capsule pulls its top edge up (see `cardTopPull`
 *    in hero-dock.ts). The region reports its height through the returned
 *    callback; here that height is capped at the slot under the header, so a
 *    taller region pushes the composer down rather than running under the header.
 *  - `--thread-h`, the thread's height (`phoneThreadHeight`).
 *
 * Everywhere else (desktop, the header bar, a page whose bar has no hero copy
 * above it) neither cap nor `--thread-h` is set, and the region is clamped by
 * `--hero-slot-max` / 42vh as it always was.
 *
 * This hook must run BEFORE the keyboard-lift effect in text-area-search-bar.tsx:
 * both listen to the visual viewport's `resize`, and the lift measures the card
 * the moment the event arrives, so the thread has to have its keyboard height by
 * then or the lift aims at a card that is about to shrink.
 *
 * @returns the callback for CardTopRegion's `onHeightChange`
 */
export function usePhoneThreadFit({
  capsuleRef,
  barRowRef,
  composerRowRef,
  enabled,
}: {
  capsuleRef: RefObject<HTMLElement>;
  /** The row wrapping the capsule: its top never moves with the card. */
  barRowRef: RefObject<HTMLElement>;
  /** The textarea + footer: what the thread's height has to leave room for. */
  composerRowRef: RefObject<HTMLElement>;
  enabled: boolean;
}): (height: number) => void {
  const state = useRef<{ regionHeight: number; pullCap: number | null }>({ regionHeight: 0, pullCap: null });

  const writePull = useCallback(() => {
    const { regionHeight, pullCap } = state.current;
    capsuleRef.current?.style.setProperty(
      '--card-top-h',
      `${Math.round(cardTopPull(regionHeight, pullCap) * 100) / 100}px`,
    );
  }, [capsuleRef]);

  const onCardTopHeight = useCallback(
    (height: number) => {
      state.current.regionHeight = height;
      writePull();
    },
    [writePull],
  );

  useEffect(() => {
    if (!enabled) return;
    const viewport = window.visualViewport;
    let lastThreadHeight = '';
    let frame = 0;

    const release = () => {
      state.current.pullCap = null;
      lastThreadHeight = '';
      capsuleRef.current?.style.removeProperty('--thread-h');
      writePull();
    };

    const fit = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      const capsule = capsuleRef.current;
      const row = barRowRef.current;
      const hero = document.getElementById('main-search-bar');
      if (!capsule || !row || !hero) return;

      // Read as the layout WILL be: the hero copy is still collapsing on a first
      // focus and the thread may be mid-resize; aiming at the moment's rects
      // would land short of where both come to rest.
      const read = readSettledLayout(hero, () => {
        const shift = new DOMMatrixReadOnly(getComputedStyle(hero).transform).m42;
        const rowRect = row.getBoundingClientRect();
        const slot = heroSlotMax(rowRect.top, hero.getBoundingClientRect().top);
        const region = capsule.querySelector<HTMLElement>('[data-search-card-top]');
        return {
          slot,
          // Document coordinates, with the block's own lift taken back out.
          rowTop: rowRect.top - document.documentElement.getBoundingClientRect().top - shift,
          chrome: capsule.getBoundingClientRect().height - (region?.clientHeight ?? 0),
          // The "Might interest you" bar at the foot of the card (its own part of
          // the chrome): the keyboard covers it, so it must not cost the thread
          // room in the strip the keyboard leaves.
          peekBar: capsule.querySelector<HTMLElement>('[data-shelf-peek-host]')?.offsetHeight ?? 0,
          headerBand: parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0,
        };
      });

      // No hero copy above the bar (the regional hub's inline composer): the
      // slot sits on its floor and the card opens over the page, not the screen.
      if (read.slot <= HERO_SLOT_MIN_PX) {
        release();
        return;
      }

      const pullCap = read.slot + CARD_TOP_BORDER_PX;
      state.current.pullCap = pullCap;
      writePull();

      const keyboardUp =
        !!viewport &&
        keyboardInsetPx(window.innerHeight, viewport.height, viewport.scale) > KEYBOARD_OPEN_THRESHOLD_PX;
      const threadHeight = phoneThreadHeight({
        capsuleTop: read.rowTop - pullCap,
        chrome: keyboardUp ? read.chrome - read.peekBar : read.chrome,
        headerBand: read.headerBand,
        keyboardStrip: keyboardUp && viewport ? viewport.height : null,
        keyboardGap: KEYBOARD_GAP_PX,
        headerGap: CARD_HEADER_GAP_PX,
      });
      if (threadHeight !== lastThreadHeight) {
        lastThreadHeight = threadHeight;
        capsule.style.setProperty('--thread-h', threadHeight);
      }
    };

    // Observed boxes change in response to what `fit` writes (the card follows the
    // thread's height; the composer row grows with the textarea; the copy collapses
    // on focus): deferring to a frame keeps the write out of the observer's own
    // delivery loop, and an unchanged value writes nothing, so it settles.
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(fit);
    };

    fit();
    const observer = new ResizeObserver(schedule);
    const copy = document.getElementById('search-content-container');
    if (copy) observer.observe(copy);
    // The card itself: the bar at its foot mounts and unmounts with the picks,
    // and the thread must give up (or take back) exactly that much height.
    if (capsuleRef.current) observer.observe(capsuleRef.current);
    if (composerRowRef.current) observer.observe(composerRowRef.current);
    window.addEventListener('resize', schedule, { passive: true });
    // Synchronous, not scheduled: see the note on ordering above.
    viewport?.addEventListener('resize', fit);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      viewport?.removeEventListener('resize', fit);
      release();
    };
  }, [enabled, capsuleRef, barRowRef, composerRowRef, writePull]);

  return onCardTopHeight;
}
